import React, { useState, useEffect } from 'react';

import Footer from '../../components/footer/index';
import Diskon from '../../components/layanan/it_support/paket';
import { useDarkMode } from '../../components/darkmode/DarkModeContext'; // Import dark mode context
import { Headphones, Shield, Cpu, BarChart3, Wifi, Server, Clock, Users, CheckCircle, Zap, MessageCircle, ArrowRight, Monitor, Network, Activity } from 'lucide-react';

const ITSupportPage = () => {
  // Use dark mode from context instead of local state
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('support');

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (serviceName = "IT Support") => {
    const phoneNumber = "6287778882022"; // Ganti dengan nomor WhatsApp perusahaan
    const message = `Halo, saya tertarik dengan layanan ${serviceName}. Bisakah saya mendapatkan informasi lebih lanjut?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  const services = {
    support: [
      {
        icon: <Headphones className="w-10 h-10" />,
        title: "Help Desk Support",
        description: "Layanan dukungan teknis 24/7 untuk menangani masalah IT sehari-hari",
        features: ["Response time < 15 menit", "Dukungan via phone/email/chat", "Ticketing system terintegrasi", "Eskalasi prioritas"],
        image: "/image/layanan/help-desk.png"
      },
      {
        icon: <Shield className="w-10 h-10" />,
        title: "Security Maintenance",
        description: "Pemeliharaan keamanan sistem dan jaringan dari ancaman cyber",
        features: ["Patch management", "Virus & malware protection", "Firewall management", "Security audit berkala"],
        image: "/image/layanan/security-maintenance.png"
      },
      {
        icon: <Cpu className="w-10 h-10" />,
        title: "System Maintenance",
        description: "Perawatan rutin sistem untuk menjaga performa optimal",
        features: ["System optimization", "Backup & recovery", "Hardware maintenance", "Software updates"],
        image: "/image/layanan/system-maintenance.png"
      },
      {
        icon: <Server className="w-10 h-10" />,
        title: "Network Management",
        description: "Manajemen dan optimasi infrastruktur jaringan",
        features: ["Network monitoring", "Troubleshooting jaringan", "Configuration management", "Performance tuning"],
        image: "/image/layanan/network-management.png"
      }
    ],
    monitoring: [
      {
        icon: <BarChart3 className="w-10 h-10" />,
        title: "Bandwidth Monitoring",
        description: "Pemantauan penggunaan bandwidth secara real-time",
        features: ["Real-time traffic analysis", "Usage reporting", "Threshold alerts", "Historical data analysis"],
        image: "/image/layanan/bandwidth-monitoring.png"
      },
      {
        icon: <Activity className="w-10 h-10" />,
        title: "Traffic Management",
        description: "Pengelolaan lalu lintas jaringan untuk optimasi performa",
        features: ["QoS implementation", "Traffic shaping", "Application prioritization", "Load balancing"],
        image: "/image/layanan/traffic-management.png"
      },
      {
        icon: <Monitor className="w-10 h-10" />,
        title: "Performance Monitoring",
        description: "Monitoring performa jaringan dan sistem secara menyeluruh",
        features: ["Uptime monitoring", "Performance metrics", "Alert notifications", "Capacity planning"],
        image: "/image/layanan/performance-monitoring.png"
      },
      {
        icon: <Network className="w-10 h-10" />,
        title: "Network Analytics",
        description: "Analisis mendalam untuk pengambilan keputusan strategis",
        features: ["Trend analysis", "Capacity forecasting", "Cost optimization", "Strategic reporting"],
        image: "/image/layanan/network-analytics.png"
      }
    ]
  };

  const benefits = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "24/7 Support",
      description: "Layanan dukungan teknis tersedia 24 jam sehari, 7 hari seminggu"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Response Time Cepat",
      description: "Waktu respons cepat untuk semua permintaan dukungan teknis"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Keamanan Terjamin",
      description: "Sistem keamanan yang komprehensif untuk melindungi infrastruktur Anda"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Optimasi Bandwidth",
      description: "Manajemen bandwidth yang efisien untuk performa optimal"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Tim Ahli Bersertifikat",
      description: "Didukung oleh tim teknisi profesional yang bersertifikat"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "SLA Terjamin",
      description: "Service Level Agreement yang jelas dan terjamin"
    }
  ];

  const pricingPlans = [
    {
      name: "Basic Support",
      popular: false,
      price: "Mulai dari Rp 1.5jt/bulan",
      features: [
        "8x5 Support (Senin-Jumat)",
        "Response time < 4 jam",
        "Remote support",
        "Basic monitoring",
        "Laporan bulanan"
      ]
    },
    {
      name: "Professional Support",
      popular: true,
      price: "Mulai dari Rp 3.5jt/bulan",
      features: [
        "24/7 Support",
        "Response time < 1 jam",
        "Remote & on-site support",
        "Advanced monitoring",
        "Manajemen bandwidth",
        "Laporan mingguan",
        "SLA 99.5%"
      ]
    },
    {
      name: "Enterprise Support",
      popular: false,
      price: "Kustom",
      features: [
        "24/7 Dedicated team",
        "Response time < 15 menit",
        "Full management service",
        "Advanced security",
        "Custom bandwidth management",
        "Laporan real-time",
        "SLA 99.9%",
        "Consulting services"
      ]
    }
  ];

  const technologies = [
    { name: "PRTG Network Monitor", logo: "/tech/prtg.png" },
    { name: "SolarWinds", logo: "/tech/solarwinds.png" },
    { name: "Cacti", logo: "/tech/cacti.png" },
    { name: "Zabbix", logo: "/tech/zabbix.png" },
    { name: "Nagios", logo: "/tech/nagios.png" },
    { name: "ManageEngine", logo: "/tech/manageengine.png" }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'
    }`}>
      
      
      
      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : ''
        }`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "url('/image/it-support-dark.jpg')"
            : "url('/image/it-support.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-r from-gray-900/80 to-gray-800/80' 
            : 'bg-gradient-to-r from-[#1E9B4C]/20 to-green-700/20'
        }`}></div>

        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${
              isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
            } px-4 py-2 rounded-full text-sm font-medium mb-6`}>
              <Headphones className="w-4 h-4" />
              Professional IT Services
            </div>

            <h1
              className={`text-4xl md:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              IT Support &
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
                Bandwidth Management
              </span>
            </h1>

            <p
              className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Layanan dukungan teknis profesional untuk pemeliharaan sistem keamanan dan jaringan Anda, 
              termasuk monitoring dan manajemen bandwidth untuk memastikan efisiensi dan keandalan jaringan.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => contactViaWhatsApp("IT Support")}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </div>
      </section>

       <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Services Tabs */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Layanan Kami
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Solusi lengkap untuk semua kebutuhan IT support dan manajemen jaringan Anda
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="flex justify-center mb-8">
            <div
              className={`inline-flex rounded-lg p-1 shadow-lg ${
                isDarkMode 
                  ? 'bg-gray-700 border border-gray-600' 
                  : 'bg-white border border-gray-200'
              }`}
            >
              <button
                onClick={() => setActiveTab('support')}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === 'support'
                    ? 'bg-[#1E9B4C] text-white shadow-lg'
                    : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                IT Support
              </button>
              <button
                onClick={() => setActiveTab('monitoring')}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === 'monitoring'
                    ? 'bg-[#1E9B4C] text-white shadow-lg'
                    : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Bandwidth Management
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services[activeTab].map((service, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl ${
                  isDarkMode 
                    ? 'bg-gray-700 border border-gray-600' 
                    : 'bg-white border border-gray-100'
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${
                    isDarkMode ? 'from-gray-900/70 to-transparent' : 'from-black/40 to-transparent'
                  }`}></div>
                  <div className="absolute bottom-4 left-4">
                    <div className={`p-3 rounded-lg ${
                      isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'
                    } backdrop-blur-sm inline-flex`}>
                      {service.icon}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {service.title}
                  </h3>
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {service.description}
                  </p>
                  
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => contactViaWhatsApp(service.title)}
                    className={`mt-6 w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-green-700 hover:bg-green-600 text-white'
                        : 'bg-[#1E9B4C] hover:bg-green-700 text-white'
                    }`}
                  >
                    Info Lebih Lanjut
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Keunggulan Layanan Kami
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Mengapa harus memilih layanan IT support dan monitoring dari kami
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C]'
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-md'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
                }`}>
                  {benefit.icon}
                </div>
                <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {benefit.title}
                </h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Updated for mobile responsiveness */}
      <Diskon />
      {/* CTA Section */}
      <section
        className={`py-16 px-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
        }`}
      >
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Siap Mengoptimalkan Infrastruktur IT Anda?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Hubungi kami sekarang untuk konsultasi gratis dan dapatkan solusi IT support 
            dan manajemen bandwidth terbaik untuk bisnis Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("IT Support")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <Headphones className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("Enterprise Solution")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Enterprise Solution
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ITSupportPage;